import { v4 as uuidv4 } from 'uuid';
import getApiConfig from '../config/apiConfig';
import { emailTemplateService } from './emailTemplateService';
import adminService from './adminService';
import salesPersonService from './salesPersonService';

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error);
  }
  return response.json();
};

const emailService = {
  // Send email
  async sendEmail(emailData) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/email/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(emailData)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error sending email:', error);
      throw error;
    }
  },

  // Send email with template
  async sendEmailWithTemplate(templateName, to, cc, bcc, tokens) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/email/send-template`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          templateName,
          to,
          cc,
          bcc,
          tokens
        })
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error sending email with template:', error);
      throw error;
    }
  },

  // Get email history
  async getEmailHistory() {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/email/history`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error getting email history:', error);
      throw error;
    }
  },

  // Get email by ID
  async getEmail(emailId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/email/${emailId}`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error getting email:', error);
      throw error;
    }
  },

  // Send ticket notification using template
  async sendTicketNotificationWithTemplate(ticketData, contractData, contactEmails, ticketAction, processedTemplate) {
    try {
      if (!contactEmails || contactEmails.length === 0) {
        throw new Error('No contact emails provided');
      }

      if (!processedTemplate) {
        throw new Error('No template provided');
      }

      const { subject, body, bcc, Subject, TemplateBody, templateBody, Bcc } = processedTemplate;
      
      // Use the correct property names
      const emailSubject = subject || Subject;
      const emailBody = body || templateBody || TemplateBody;
      const emailBcc = bcc || Bcc;
      
      let bccRecipients = [];
      if (emailBcc) {
        bccRecipients = emailBcc.split(',')
          .map(email => email.trim())
          .filter(email => email && email.includes('@'));
      }

      const results = [];
      for (const email of contactEmails) {
        const emailData = {
          to: [email],
          subject: emailSubject,
          body: emailBody,
          bcc: bccRecipients
        };

        try {
          const response = await this.sendEmail(emailData);
          results.push({ email, success: true, response });
        } catch (error) {
          results.push({ email, success: false, error: error.message });
        }
      }

      return results;
    } catch (error) {
      console.error('Error in sendTicketNotificationWithTemplate:', error);
      throw error;
    }
  },

  async sendTicketNotification(ticketData, contractData, contactEmails, ticketAction) {
    try {
      // Get the email template
      const template = await emailTemplateService.getEmailTemplateByName('Support Ticket Notification');
      
      // Prepare tokens for replacement
      const tokens = {
        tickettitle: ticketData.title,
        supportcontractname: contractData.contractName,
        ticketaction: ticketAction,
        ticketdetail: ticketData.ticketDetail
      };

      // Replace tokens in template
      const processedTemplate = emailTemplateService.replaceTokens(template, tokens);

      // Filter out invalid email addresses with better validation
      const validEmails = contactEmails.filter(email => {
        if (!email || typeof email !== 'string') return false;
        // Basic email validation: must contain @ and . and have reasonable format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email.trim());
      });

      if (validEmails.length === 0) {
        return { 
          success: true, 
          message: 'No contacts with valid email addresses found - no notification sent',
          recipients: []
        };
      }

      // Send email using sendTicketNotificationWithTemplate
      const results = await this.sendTicketNotificationWithTemplate(
        ticketData,
        contractData,
        validEmails,
        ticketAction,
        processedTemplate
      );

      // Transform array results to single object format for frontend compatibility
      const successfulSends = results.filter(r => r.success);
      const failedSends = results.filter(r => !r.success);

      return {
        success: successfulSends.length > 0,
        message: `Support ticket notification sent to ${successfulSends.length} recipient(s)${failedSends.length > 0 ? `, ${failedSends.length} failed` : ''}`,
        recipients: successfulSends.map(r => r.email),
        failedSends: failedSends
      };
    } catch (error) {
      console.error('Error in sendTicketNotification:', error);
      throw error;
    }
  },

  // Helper method to determine ticket action
  getTicketAction(oldStatus, newStatus, isNewTicket = false) {
    if (isNewTicket) return 'created';
    if (oldStatus === newStatus) return 'updated';
    return `changed from ${oldStatus} to ${newStatus}`;
  },

  // Send opportunity notification using template
  async sendOpportunityNotification(opportunityData, isTestOpportunity = false) {
    try {
      // Get the email template
      const template = await emailTemplateService.getEmailTemplateByName('New Opportunity Notification');
      
      // Prepare tokens for replacement
      const opportunityId = opportunityData.opportunityId || opportunityData.id;
      const opportunityUrl = `${window.location.origin}/opportunities/${opportunityId}`;
      
      const tokens = {
        opportunityId: opportunityId,
        opportunityurl: opportunityUrl,
        createdby: opportunityData.createdBy || opportunityData.createdByEmail || 'Unknown User'
      };

      // Replace tokens in template
      const processedTemplate = emailTemplateService.replaceTokens(template, tokens);

      // Determine recipients based on test opportunity checkbox
      let recipientEmails = [];
      
      if (isTestOpportunity) {
        // Send to admin users only
        const adminUsers = await adminService.getAdminUsers();
        recipientEmails = adminUsers.map(admin => admin.UserId);
      } else {
        // Send to all salespersons
        const salesPersons = await salesPersonService.getSalesPersons();
        recipientEmails = salesPersons.map(sp => sp.Email || sp.email);
      }

      // Filter out invalid email addresses
      const validEmails = recipientEmails.filter(email => {
        if (!email || typeof email !== 'string') return false;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email.trim());
      });
      
      if (validEmails.length === 0) {
        return { 
          success: true, 
          message: 'No valid email addresses found - no notification sent',
          recipients: []
        };
      }

      // Send email using sendOpportunityNotificationWithTemplate
      return await this.sendOpportunityNotificationWithTemplate(
        opportunityData,
        validEmails,
        processedTemplate
      );
    } catch (error) {
      console.error('Error in sendOpportunityNotification:', error);
      throw error;
    }
  },

  // Send opportunity notification with template
  async sendOpportunityNotificationWithTemplate(opportunityData, recipientEmails, processedTemplate) {
    try {
      if (!recipientEmails || recipientEmails.length === 0) {
        throw new Error('No recipient emails provided');
      }

      if (!processedTemplate) {
        throw new Error('No template provided');
      }

      const { subject, body, bcc, Subject, TemplateBody, templateBody, Bcc } = processedTemplate;
      
      // Use the correct property names
      const emailSubject = subject || Subject;
      const emailBody = body || templateBody || TemplateBody;
      const emailBcc = bcc || Bcc;
      
      let bccRecipients = [];
      if (emailBcc) {
        bccRecipients = emailBcc.split(',')
          .map(email => email.trim())
          .filter(email => email && email.includes('@'));
      }

      const results = [];
      for (const email of recipientEmails) {
        const emailData = {
          to: [email],
          subject: emailSubject,
          body: emailBody,
          bcc: bccRecipients
        };

        try {
          const response = await this.sendEmail(emailData);
          results.push({ email, success: true, response });
        } catch (error) {
          results.push({ email, success: false, error: error.message });
        }
      }

      const successfulSends = results.filter(r => r.success);
      const failedSends = results.filter(r => !r.success);

      return {
        success: successfulSends.length > 0,
        message: `Opportunity notification sent to ${successfulSends.length} recipient(s)${failedSends.length > 0 ? `, ${failedSends.length} failed` : ''}`,
        recipients: successfulSends.map(r => r.email),
        failedSends: failedSends
      };
    } catch (error) {
      console.error('Error in sendOpportunityNotificationWithTemplate:', error);
      throw error;
    }
  }
};

export { emailService }; 